import AddIcon from '@mui/icons-material/Add';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';

import { alpha } from '@mui/material';
import React, { useContext, useMemo, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import {
  AppConfigDetailDTO,
  CallCenterListDTO,
  NationalNumber,
  NationalNumberGroups,
  NationalNumberGroupsUpdateDTO,
  TradeEnum,
  TRADES
} from '_lib/api/apiTypes.shim';
import { WarningAlert } from '_lib/components/Alert/Alert';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { List } from '_lib/components/List/List';
import { ListItem } from '_lib/components/ListItem/ListItem';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { PhoneNumberTextField } from '_lib/components/PhoneNumberTextField/PhoneNumberTextField';
import { Stack } from '_lib/components/Stack/Stack';
import { TextField } from '_lib/components/TextField/TextField';
import { Typography } from '_lib/components/Typography/Typography';
import { useConfirmationDialog } from '_lib/hooks/ConfirmationDialogHook';
import { formatPhone } from '_lib/utils/helpers';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { AuthContext } from '../auth/authContext';
import { TradeIcon } from '../Trades/TradesDisplay';

type NationalNumberGroup = NationalNumberGroups[number];

type EditingGroup = Omit<NationalNumberGroup, 'id' | 'nationalNumbers'> & {
  id?: string;
  nationalNumbers: NationalNumber[];
  isNew?: boolean;
};

type NationalNumbersConfigManagementProps = {
  appConfigDetail: AppConfigDetailDTO;
};

export const NationalNumbersConfigManagement = (props: NationalNumbersConfigManagementProps) => {
  const { appConfigDetail } = props;

  const [editItem, setEditItem] = useState<EditingGroup | null>(null);
  const [errors, setErrors] = useState<string | null>(null);

  const api = useContext(ApiContext);
  const { callCenterCacheResource } = useContext(AuthContext);
  const { enqueueSnackbar } = useOurSnackbar();
  const confirmationDialog = useConfirmationDialog();

  const callCenterCacheQuery = callCenterCacheResource.useCacheQuery();

  const callCentersQuery = api.callCenters.useUnpagedList({});

  const appConfigUpdateMutation = api.appConfig.useUpdateAppConfig();

  const sortedGroups = useMemo(() => {
    if (!appConfigDetail.nationalNumberGroups) return [];
    return [...appConfigDetail.nationalNumberGroups].sort((a, b) =>
      a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })
    );
  }, [appConfigDetail.nationalNumberGroups]);

  const callCenterName = (callCenterId: string) => {
    const callCenter = callCentersQuery.data?.find((cc: CallCenterListDTO) => cc.id === callCenterId);
    return callCenter ? callCenter.name : 'Unknown Call Center';
  };

  const handleAddGroup = () => {
    if (!callCenterCacheQuery.data) {
      enqueueSnackbar('Cannot add national number group: Call center cache not loaded');
      return;
    }
    setEditItem({
      isNew: true,
      name: '',
      callCenterId: callCenterCacheQuery.data.id,
      nationalNumbers: [{ name: '', phoneNumber: '', trade: TradeEnum.Electric }]
    });
    setErrors(null);
  };

  const handleEdit = (group: NationalNumberGroup) => {
    setEditItem({ ...group });
    setErrors(null);
  };

  const handleCancelEdit = () => {
    setEditItem(null);
    setErrors(null);
  };

  const saveGroups = async (groups: EditingGroup[]) => {
    try {
      setErrors(null);

      await appConfigUpdateMutation.put({
        url: 'national-number-groups',
        data: { nationalNumberGroups: groups } as NationalNumberGroupsUpdateDTO
      });

      enqueueSnackbar('National number groups saved successfully');
      setEditItem(null);
    } catch (err) {
      if (err instanceof BadRequestError) {
        setErrors(
          err.responseData.nonFieldErrors
            .concat(err.responseData.fieldIssues.map((fi) => `${fi.path}: ${fi.message}`))
            .join('; ')
        );
      }
      enqueueSnackbar('Failed to save national number groups');
    }
  };

  const handleDeleteGroup = async (group: NationalNumberGroup) => {
    const result = await confirmationDialog.show({
      titleText: 'Confirm Delete',
      contentText: 'Are you sure you want to delete this national number group? This action cannot be undone.'
    });

    if (result?.action !== 'CONFIRM') return;

    const updatedGroups = sortedGroups.filter((g) => g.id !== group.id);
    await saveGroups(updatedGroups);
  };

  const handleAddNumber = () => {
    if (!editItem) return;
    setEditItem({
      ...editItem,
      nationalNumbers: [...editItem.nationalNumbers, { name: '', phoneNumber: '', trade: TradeEnum.Electric }]
    });
  };

  const handleRemoveNumber = (index: number) => {
    if (!editItem) return;
    const updatedNumbers = [...editItem.nationalNumbers];
    updatedNumbers.splice(index, 1);
    setEditItem({ ...editItem, nationalNumbers: updatedNumbers });
  };

  const handleNumberChange = (index: number, field: keyof NationalNumber, value: string | TradeEnum) => {
    if (!editItem) return;
    const updatedNumbers = [...editItem.nationalNumbers];
    updatedNumbers[index] = { ...updatedNumbers[index], [field]: value } as NationalNumber;
    setEditItem({ ...editItem, nationalNumbers: updatedNumbers });
  };

  const handleSaveEdits = async () => {
    if (!editItem) return;

    if (editItem.isNew) {
      const updatedGroups = [...sortedGroups, editItem];
      await saveGroups(updatedGroups);
    } else {
      const updatedGroups = sortedGroups.map((g) => (g.id === editItem.id ? editItem : g));
      await saveGroups(updatedGroups);
    }
  };

  const isLoading = appConfigUpdateMutation.isPending || callCenterCacheQuery.isFetching || callCentersQuery.isFetching;

  return (
    <FlexBox
      sx={{
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative'
      }}>
      <BackdropSpinner open={isLoading} />
      <FlexBox
        sx={{
          overflow: 'auto',
          width: '100%',
          height: '100%',
          paddingX: editItem ? 2 : 1.5,
          paddingY: editItem ? 0 : 1.5
        }}>
        {!editItem && (
          <>
            {!sortedGroups.length && !isLoading && (
              <WarningAlert>
                <Typography>No national number groups found.</Typography>
              </WarningAlert>
            )}
            {sortedGroups.length > 0 && (
              <List sx={{ width: '100%' }} disablePadding>
                {sortedGroups.map((group, index) => (
                  <ListItem
                    key={group.id}
                    divider={index !== sortedGroups.length - 1}
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'flex-start'
                    }}>
                    <FlexBox
                      sx={(theme) => ({
                        backgroundColor: theme.palette.primary.light,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        paddingX: 1,
                        borderTopLeftRadius: 2,
                        borderTopRightRadius: 2
                      })}>
                      <Typography>
                        <Typography component='span' text={group.name} variant='body1Bold' color='primaryInverted' />
                        <Typography component='span' text=' | ' color='primaryInverted' />
                        <Typography
                          component='span'
                          text={callCenterName(group.callCenterId)}
                          color='primaryInverted'
                        />
                      </Typography>
                      <Stack useFlexGap direction='row' spacing={0}>
                        <IconButton
                          onClick={() => handleEdit(group)}
                          disabled={!!editItem}
                          size='small'
                          color='primaryInverted'>
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteGroup(group)}
                          disabled={!!editItem}
                          size='small'
                          color='primaryInverted'>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </FlexBox>
                    <List
                      sx={(theme) => ({
                        width: '100%',
                        paddingLeft: 1,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                      })}
                      disablePadding>
                      {group.nationalNumbers.map((nn, idx) => (
                        <ListItem key={idx} disableGutters sx={{ paddingY: 0.5 }}>
                          <Stack useFlexGap direction='row' spacing={1} sx={{ alignItems: 'center' }}>
                            <TradeIcon trade={nn.trade} color='primary' />
                            <Typography text={formatPhone(nn.phoneNumber)} />
                            <Typography text='|' />
                            <Typography text={nn.name} />
                          </Stack>
                        </ListItem>
                      ))}
                    </List>
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
        {editItem && (
          <Stack useFlexGap spacing={2} sx={{ width: '100%', marginTop: 2 }}>
            <Stack useFlexGap direction='row' spacing={2}>
              <TextField
                label='Group Name'
                value={editItem.name}
                onChange={(e) => setEditItem({ ...editItem, name: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label='Call Center'
                value={editItem.callCenterId}
                onChange={(e) => setEditItem({ ...editItem, callCenterId: e.target.value })}
                required
                select
                disabled={!editItem.isNew}
                fullWidth>
                {callCentersQuery.data?.map((cc: CallCenterListDTO) => (
                  <MenuItem key={cc.id} value={cc.id}>
                    {cc.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Divider />
            <List disablePadding sx={{ width: '100%' }}>
              {editItem.nationalNumbers.map((nn, index) => (
                <ListItem key={index} disableGutters>
                  <Stack useFlexGap direction='row' spacing={2} sx={{ width: '100%', alignItems: 'flex-start' }}>
                    <PhoneNumberTextField
                      label='Phone Number'
                      value={nn.phoneNumber}
                      onChange={(e) => handleNumberChange(index, 'phoneNumber', e.target.value)}
                      required
                      sx={{ width: '150px' }}
                    />
                    <TextField
                      label='National Number Name'
                      value={nn.name}
                      onChange={(e) => handleNumberChange(index, 'name', e.target.value)}
                      required
                      fullWidth
                      slotProps={{ htmlInput: { maxLength: 50 } }}
                      sx={{ maxWidth: '400px' }}
                    />
                    <TextField
                      label='Trade'
                      value={nn.trade}
                      onChange={(e) => handleNumberChange(index, 'trade', Number(e.target.value) as TradeEnum)}
                      required
                      select
                      sx={{ minWidth: '150px' }}>
                      {TRADES.map((trade) => (
                        <MenuItem key={trade} value={trade}>
                          <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
                            <TradeIcon trade={trade} />
                            <Typography text={TradeEnum[trade]} />
                          </Stack>
                        </MenuItem>
                      ))}
                    </TextField>
                    <IconButton onClick={() => handleRemoveNumber(index)} color='error'>
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </ListItem>
              ))}
              <ListItem disableGutters>
                <Button
                  startIcon={<AddCircleIcon />}
                  text='Number'
                  variant='contained'
                  onClick={handleAddNumber}
                  sx={{ textTransform: 'none' }}
                />
              </ListItem>
            </List>
          </Stack>
        )}
      </FlexBox>
      {errors && <ErrorDisplay errors={[errors]} sx={{ borderRadius: 1 }} />}
      <Divider />
      <FlexBox sx={{ width: '100%', height: '48px' }}>
        {!editItem && (
          <Button
            startIcon={<AddIcon />}
            text='Group'
            variant='contained'
            sx={{ textTransform: 'none', marginLeft: 'auto', marginY: 1, marginRight: 1 }}
            onClick={handleAddGroup}
          />
        )}
        {editItem && (
          <Stack useFlexGap direction='row' spacing={1} sx={{ marginLeft: 'auto', marginY: 1, marginRight: 1 }}>
            <Button
              startIcon={<CancelIcon />}
              color='error'
              text='Cancel'
              variant='contained'
              sx={{ textTransform: 'none' }}
              onClick={handleCancelEdit}
            />
            <Button
              startIcon={<SaveIcon />}
              color='primary'
              text='Save'
              variant='contained'
              sx={{ textTransform: 'none' }}
              onClick={handleSaveEdits}
            />
          </Stack>
        )}
      </FlexBox>
      {confirmationDialog.element}
    </FlexBox>
  );
};
